import { apiService } from "@/services";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { handleApiError } from "@/utils";
import { DEFAULT_STALE_TIME } from "@/constants";
import type {
  KeywordTheme,
  KeywordThemeKeyword,
  ThemeSuggestion,
  KeywordSuggestion,
  CreateKeywordThemeParams,
  UpdateKeywordThemeParams,
  AddKeywordToThemeParams,
  UpdateKeywordInThemeParams,
  RemoveKeywordFromThemeParams,
} from "@/types/keyword-theme";

export const keywordThemesQueryKey = (productId: number) =>
  ["product", "keyword-themes", String(productId)] as const;

export const autoThemeSuggestionsQueryKey = (productId: number) =>
  ["product", "keyword-themes", "auto-suggestions", String(productId)] as const;

const ANALYSIS_POLL_INTERVAL_MS = 5000;

export const useGetKeywordThemes = (productId: number) =>
  useQuery<KeywordTheme[]>({
    queryKey: keywordThemesQueryKey(productId),
    queryFn: () =>
      apiService.get<KeywordTheme[]>(`/products/${productId}/keyword-themes`),
    enabled: productId > 0,
    staleTime: DEFAULT_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const themes = query.state.data;
      if (!themes) return false;
      const hasPending = themes.some((t) => t.keywords.length > 0 && !t.ai_summary);
      return hasPending ? ANALYSIS_POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: true,
  });

export const useCreateKeywordTheme = () =>
  useMutation<KeywordTheme, unknown, CreateKeywordThemeParams>({
    mutationFn: ({ productId, name }) =>
      apiService.post<KeywordTheme, { name: string }>(
        `/products/${productId}/keyword-themes`,
        { name },
      ),
    onSuccess: (_, { productId }) => {
      // A new theme changes existing_theme_names/keywords_in_existing_themes
      // for the 5a prompt -- the auto-fetched suggestions are stale now.
      queryClient.invalidateQueries({ queryKey: autoThemeSuggestionsQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useUpdateKeywordTheme = () =>
  useMutation<KeywordTheme, unknown, UpdateKeywordThemeParams>({
    mutationFn: ({ productId, themeId, ...body }) =>
      apiService.patch<KeywordTheme, Partial<UpdateKeywordThemeParams>>(
        `/products/${productId}/keyword-themes/${themeId}`,
        body,
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useDeleteKeywordTheme = () =>
  useMutation<void, unknown, { productId: number; themeId: number }>({
    mutationFn: ({ productId, themeId }) =>
      apiService.delete<void, unknown>(
        `/products/${productId}/keyword-themes/${themeId}`,
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
      // Dropping below 3 themes re-enables auto-suggest -- and the freed-up
      // keywords change what a fresh 5a call would even see.
      queryClient.invalidateQueries({ queryKey: autoThemeSuggestionsQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useAddKeywordToTheme = () =>
  useMutation<KeywordThemeKeyword, unknown, AddKeywordToThemeParams>({
    mutationFn: ({ productId, themeId, keywordId, is_head_term }) =>
      apiService.post<KeywordThemeKeyword, { keyword_id: number; is_head_term?: boolean }>(
        `/products/${productId}/keyword-themes/${themeId}/keywords`,
        { keyword_id: keywordId, is_head_term },
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(productId)] });
    },
    onError: (error) => handleApiError(error),
  });

export const useBulkAddKeywordsToTheme = () =>
  useMutation<{ added_count: number }, unknown, { productId: number; themeId: number; keywordIds: number[] }>({
    mutationFn: ({ productId, themeId, keywordIds }) =>
      apiService.post<{ added_count: number }, { keyword_ids: number[] }>(
        `/products/${productId}/keyword-themes/${themeId}/keywords/bulk`,
        { keyword_ids: keywordIds },
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(productId)] });
    },
    onError: (error) => handleApiError(error),
  });

export const useUpdateKeywordInTheme = () =>
  useMutation<KeywordThemeKeyword, unknown, UpdateKeywordInThemeParams>({
    mutationFn: ({ productId, themeId, keywordId, is_head_term }) =>
      apiService.patch<KeywordThemeKeyword, { is_head_term: boolean }>(
        `/products/${productId}/keyword-themes/${themeId}/keywords/${keywordId}`,
        { is_head_term },
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useRemoveKeywordFromTheme = () =>
  useMutation<void, unknown, RemoveKeywordFromThemeParams>({
    mutationFn: ({ productId, themeId, keywordId }) =>
      apiService.delete<void, unknown>(
        `/products/${productId}/keyword-themes/${themeId}/keywords/${keywordId}`,
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useGenerateThemeSuggestions = () =>
  useMutation<{ suggestions: ThemeSuggestion[] }, unknown, { productId: number }>({
    mutationFn: ({ productId }) =>
      apiService.post<{ suggestions: ThemeSuggestion[] }, Record<string, never>>(
        `/products/${productId}/keyword-themes/suggestions`,
        {},
      ),
    onError: (error) => handleApiError(error),
  });

/**
 * Spec 5a: while a product has fewer than 3 themes, Signal AI is supposed to
 * surface thematic groupings "automatically" -- not gated behind a person
 * clicking "Ask Signal AI" first. Fetches on mount/whenever `enabled` flips
 * true (via the same react-query cache every other read here already uses,
 * not a scheduled background job), gated by the caller on
 * `existingThemesCount < 3` so it stops firing entirely once that "person
 * has to ask for more" phase kicks in.
 */
export const useAutoThemeSuggestions = (productId: number, enabled: boolean) =>
  useQuery<{ suggestions: ThemeSuggestion[] }>({
    queryKey: autoThemeSuggestionsQueryKey(productId),
    queryFn: () =>
      apiService.post<{ suggestions: ThemeSuggestion[] }, Record<string, never>>(
        `/products/${productId}/keyword-themes/suggestions`,
        {},
      ),
    enabled: enabled && productId > 0,
    staleTime: DEFAULT_STALE_TIME,
    // A real Gemini call sits behind this -- don't re-trigger it just
    // because the browser tab regained focus.
    refetchOnWindowFocus: false,
  });

export const useAcceptThemeSuggestion = () =>
  useMutation<
    { id: number; name: string },
    unknown,
    { productId: number; name: string; keyword_ids: number[]; head_term_keyword_id?: number }
  >({
    mutationFn: ({ productId, ...body }) =>
      apiService.post<{ id: number; name: string }, typeof body>(
        `/products/${productId}/keyword-themes/accept-suggestion`,
        body,
      ),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(productId)] });
      queryClient.invalidateQueries({ queryKey: autoThemeSuggestionsQueryKey(productId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useGenerateKeywordSuggestions = () =>
  useMutation<{ keywords: KeywordSuggestion[] }, unknown, { productId: number; themeId: number }>({
    mutationFn: ({ productId, themeId }) =>
      apiService.post<{ keywords: KeywordSuggestion[] }, Record<string, never>>(
        `/products/${productId}/keyword-themes/${themeId}/keyword-suggestions`,
        {},
      ),
    onError: (error) => handleApiError(error),
  });

export const useRegenerateThemeAnalysis = () =>
  useMutation<{ status: string }, unknown, { productId: number; themeId: number }>({
    mutationFn: ({ productId, themeId }) =>
      apiService.post<{ status: string }, Record<string, never>>(
        `/products/${productId}/keyword-themes/${themeId}/regenerate-analysis`,
        {},
      ),
    onSuccess: (_, { productId, themeId }) => {
      // Clear ai_summary optimistically so "Generating…" shows immediately;
      // the refetchInterval polling will pick up the result when the job completes.
      queryClient.setQueryData<KeywordTheme[]>(
        keywordThemesQueryKey(productId),
        (old) => old?.map((t) => t.id === themeId ? { ...t, ai_summary: null } : t) ?? old,
      );
    },
    onError: (error) => handleApiError(error),
  });
